'use client';
import React, { Suspense, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import LoginForm from './LoginForm';

import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { isRecoveryInProgress } from '@/lib/auth/passwordRecovery';

const roleRedirectMap: Record<string, string> = {
  super_admin: '/super-admin-dashboard',
  admin: '/dashboard',
  manager: '/vehicle-management',
  sales: '/sales-orders',
  operator: '/vehicle-management',
  viewer: '/dashboard',
};

/** Redirect already-authenticated users away from login/signup (covers bfcache / client nav). */
function AuthenticatedRedirect() {
  const { user, userRole, loading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();

  useEffect(() => {
    // A verified password-reset code signs the user in; stay here until the dialog ends that session
    if (loading || !user || isRecoveryInProgress()) return;

    const redirectParam = searchParams.get('redirect');
    const safeRedirect =
      redirectParam?.startsWith('/') && !redirectParam.startsWith('//')
        ? redirectParam
        : null;
    const destination =
      safeRedirect || (userRole && roleRedirectMap[userRole]) || '/vehicle-management';

    router.replace(destination);
  }, [loading, user, userRole, searchParams, router]);

  return null;
}

const features = [
{
  id: 'feat-vin',
  icon:
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-4">
        <path d="M9 17H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3" />
        <path d="M13 21l2-2 4 4" /><circle cx="17" cy="17" r="4" />
        <path d="M9 7h6M9 11h4" />
      </svg>,

  text: 'VIN-based inventory tracking across all locations'
},
{
  id: 'feat-analytics',
  icon:
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-4">
        <path d="M3 3v18h18" /><path d="M18 9l-5 5-4-4-3 3" />
      </svg>,

  text: 'Real-time profitability per vehicle with full cost breakdown'
},
{
  id: 'feat-global',
  icon:
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-4">
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>,

  text: 'Import pipeline monitoring with duty & customs tracking'
},
{
  id: 'feat-security',
  icon:
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-4">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>,

  text: 'Multi-tenant isolation — your data stays yours'
}];


const stats = [
{ value: '140+', label: 'Dealerships' },
{ value: '40%', label: 'Faster processing' },
{ value: '99.9%', label: 'Uptime SLA' }];


export default function AuthScreen() {
  return (
    <div className="min-h-screen flex bg-[#0a1628]">
      <Suspense fallback={null}>
        <AuthenticatedRedirect />
      </Suspense>
      {/* ── Left brand panel ── */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] flex-col justify-between p-12 relative overflow-hidden">
        {/* Animated gradient blobs — same as landing hero */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, #1d4ed8 0%, #1e3a8a 40%, transparent 70%)', animation: 'pulse 8s ease-in-out infinite' }} />

          <div
            className="absolute top-1/3 right-0 w-[400px] h-[400px] rounded-full opacity-15"
            style={{ background: 'radial-gradient(circle, #0369a1 0%, transparent 70%)', animation: 'pulse 10s ease-in-out infinite 2s' }} />

          <div
            className="absolute bottom-0 left-1/3 w-[350px] h-[350px] rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #1e40af 0%, transparent 70%)', animation: 'pulse 12s ease-in-out infinite 4s' }} />

          {/* Grid overlay */}
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
              backgroundSize: '60px 60px'
            }} />

        </div>

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          <Link href="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-blue-900 flex items-center justify-center shadow-lg shadow-blue-900/50">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" className="w-[18px] h-[18px]">
                <path d="M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3" />
                <rect x="9" y="11" width="14" height="10" rx="1" />
                <circle cx="12" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
              </svg>
            </div>
            <div>
              <span className="font-800 text-white text-xl leading-tight block tracking-tight">MotoDesk</span>
              <span className="text-blue-400 text-xs font-500">Dealer Management Platform</span>
            </div>
          </Link>
        </div>

        {/* Hero content */}
        <div className="relative">
          <h1 className="text-4xl xl:text-5xl font-800 text-white leading-[1.08] tracking-tight mb-5">
            Run your dealership
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
              without the chaos.
            </span>
          </h1>

          <p className="text-slate-300 text-base leading-relaxed mb-8 max-w-md">
            MotoDesk gives automotive dealerships a single platform to track vehicles, manage inventory, process sales, and coordinate branch transfers — from port to handover.
          </p>

          <div className="space-y-3 mb-10">
            {features?.map((f) =>
            <div key={f?.id} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-blue-900/60 border border-blue-700/40 flex items-center justify-center text-blue-400 flex-shrink-0">
                  {f?.icon}
                </div>
                <span className="text-sm text-slate-300">{f?.text}</span>
              </div>
            )}
          </div>

        </div>

        {/* Social proof */}
        <div className="relative">
          <p className="text-slate-400 text-xs italic">
            &quot;MotoDesk cut our vehicle processing time by 40%. The import flow tracker alone saved us hours every week.&quot;
          </p>
          <p className="text-slate-500 text-xs mt-0.5">— Abdul Wakeel , Osaka Wings, Kenya</p>
        </div>
      </div>
      {/* ── Right form panel ── */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 sm:px-10 lg:px-12 xl:px-16 py-12 bg-white lg:rounded-l-3xl relative">
        {/* Subtle top-right decoration */}
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-blue-50 opacity-60 -translate-y-1/2 translate-x-1/2 pointer-events-none" />

        <div className="w-full max-w-md relative">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-2.5 mb-8">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-blue-900 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" className="w-4 h-4">
                <path d="M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3" />
                <rect x="9" y="11" width="14" height="10" rx="1" />
                <circle cx="12" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
              </svg>
            </div>
            <div>
              <span className="font-800 text-slate-900 text-lg tracking-tight">MotoDesk</span>
              <span className="block text-slate-400 text-xs">Dealer Management Platform</span>
            </div>
          </div>

          <Suspense fallback={<div className="h-64 flex items-center justify-center"><div className="w-6 h-6 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" /></div>}>
              <LoginForm />
            </Suspense>

          {/* Back to landing */}
          <div className="mt-6 text-center">
            <Link href="/" className="text-xs text-slate-400 hover:text-slate-600 transition-colors inline-flex items-center gap-1">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3 h-3">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back to MotoDesk.app
            </Link>
          </div>
        </div>
      </div>
    </div>);

}